import SettingsOutlinedIcon from '@mui/icons-material/Settings';
import { Menu as MenuMui, Tooltip, IconButton, MenuItem } from '@mui/material';
import { useState } from 'react';

type MenuProps = {
  menuItems: MenuItemProps[];
  disabled?: boolean;
  tooltipTitle?: string;
};

type MenuItemProps = {
  label: string;
  onMenuItemClick: () => void;
  icon?: React.ReactNode;
  disabled?: boolean;
  dataTestId?: string;
};

export const Menu = ({ menuItems, disabled, tooltipTitle }: MenuProps) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const openMenu = Boolean(anchorEl);

  const handleMenuButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleMenuItemClick = (onMenuItemClick: () => void) => {
    onMenuItemClick();
    handleMenuClose();
  };

  return (
    menuItems && (
      <>
        <Tooltip placement="top" title={tooltipTitle ?? ''}>
          <span>
            <IconButton
              onClick={handleMenuButtonClick}
              id="menu-button"
              size="small"
              sx={{ ml: 2 }}
              aria-controls={openMenu ? 'menu' : undefined}
              aria-haspopup="true"
              aria-expanded={openMenu ? 'true' : undefined}
              data-testid="menu-button"
              disabled={disabled}
            >
              <SettingsOutlinedIcon />
            </IconButton>
          </span>
        </Tooltip>
        <MenuMui
          id="menu"
          anchorEl={anchorEl}
          open={openMenu}
          onClose={handleMenuClose}
          MenuListProps={{
            'aria-labelledby': 'menu-button',
          }}
          PaperProps={{
            elevation: 0,
            sx: {
              overflow: 'visible',
              filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
              mt: 1.5,
              '& .MuiAvatar-root': {
                width: 32,
                height: 32,
                ml: -0.5,
                mr: 1,
              },
              '&:before': {
                content: '""',
                display: 'block',
                position: 'absolute',
                top: 0,
                right: 14,
                width: 10,
                height: 10,
                bgcolor: 'background.paper',
                transform: 'translateY(-50%) rotate(45deg)',
                zIndex: 0,
              },
            },
          }}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        >
          {menuItems.map((item) => (
            <MenuItem
              key={item.label}
              onClick={() => handleMenuItemClick(item.onMenuItemClick)}
              disabled={item.disabled}
              data-testid={item.dataTestId}
            >
              {item.icon && item.icon}
              {item.label}
            </MenuItem>
          ))}
        </MenuMui>
      </>
    )
  );
};
